import { Switch } from '@headlessui/react'
import clsx from 'clsx'
import { trpc } from '@/lib/trpc'
import { useAuth } from '@/contexts/AuthContext'
import { useTheme } from '@/hooks/useTheme'
import {
  FEATURE_FLAGS,
  useFeatureFlagStore,
  useSetFeatureFlag,
  type FeatureFlagKey,
} from '@/hooks/useFeatureFlags'

/** Profile + per-account settings. Feature flags persist to the account
 *  (users.feature_flags) so they follow the user between machines. */
export function SettingsPage() {
  const { user } = useAuth()
  const me = trpc.user.me.useQuery(undefined, { staleTime: Infinity })
  const flags = useFeatureFlagStore((s) => s.flags)
  const { set, saving, error } = useSetFeatureFlag()
  const { theme, toggle: toggleTheme } = useTheme()

  const profile = me.data
  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ')
  const email = profile?.email ?? user?.email ?? ''
  const initial = (fullName || email).charAt(0).toUpperCase()

  return (
    <div className="min-h-screen bg-surface-primary px-8 pb-24 pt-28">
      <div className="mx-auto max-w-2xl">
        <h1 className="text-display-small mb-8 text-text-primary">Settings</h1>

        <Section title="Profile">
          <div className="flex items-center gap-4">
            {profile?.avatarUrl ? (
              <img src={profile.avatarUrl} alt="" className="h-14 w-14 rounded-full object-cover" />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-fill-primary text-h2 text-text-inverse">
                {initial || '?'}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-h3 truncate text-text-primary">{fullName || email}</p>
              {fullName && <p className="text-body-small truncate text-text-secondary">{email}</p>}
              {profile?.isAdmin && (
                <span className="text-label-small mt-1 inline-block rounded bg-surface-tertiary px-1.5 py-0.5 text-text-secondary">
                  Admin
                </span>
              )}
            </div>
          </div>
          {me.error && (
            <p className="text-body-small mt-3 text-fill-destructive">
              Couldn't load your profile: {me.error.message}
            </p>
          )}
        </Section>

        <Section title="Appearance">
          <Row label="Dark mode" description="Follows this browser, not your account.">
            <Toggle checked={theme === 'dark'} onChange={toggleTheme} label="Dark mode" />
          </Row>
        </Section>

        <Section title="Review tools">
          {(Object.keys(FEATURE_FLAGS) as FeatureFlagKey[]).map((key) => (
            <Row key={key} label={FEATURE_FLAGS[key].label} description={FEATURE_FLAGS[key].description}>
              <Toggle
                checked={flags[key]}
                onChange={(v) => set(key, v)}
                label={FEATURE_FLAGS[key].label}
              />
            </Row>
          ))}
          <p className="text-caption-default mt-3 min-h-4 text-text-tertiary" aria-live="polite">
            {error ? (
              <span className="text-fill-destructive">Couldn't save: {error}</span>
            ) : saving ? (
              'Saving…'
            ) : (
              'Saved to your account.'
            )}
          </p>
        </Section>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-h3 mb-3 text-text-secondary">{title}</h2>
      <div className="rounded-modal border border-border-default bg-surface-secondary p-5">{children}</div>
    </section>
  )
}

function Row({
  label,
  description,
  children,
}: {
  label: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between gap-6 border-b border-border-default py-3 first:pt-0 last:border-b-0 last:pb-0">
      <div>
        <p className="text-body-default text-text-primary">{label}</p>
        {description && <p className="text-body-small text-text-secondary">{description}</p>}
      </div>
      {children}
    </div>
  )
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
}) {
  return (
    <Switch
      checked={checked}
      onChange={onChange}
      aria-label={label}
      className={clsx(
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-fill-primary',
        checked ? 'bg-accent-blue' : 'bg-surface-tertiary'
      )}
    >
      <span
        aria-hidden="true"
        className={clsx(
          'inline-block h-5 w-5 rounded-full bg-white shadow transition-transform duration-200',
          checked ? 'translate-x-[22px]' : 'translate-x-0.5'
        )}
      />
    </Switch>
  )
}
